import { useState, useEffect } from 'react'
import type { ChangeEvent, DragEvent } from 'react'
import type { FullPokemonCard, Locale } from '@/types/pokemon'
import { getApiUrl } from '@/lib/utils'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { STANDARD_CONDITIONS, type StandardCondition } from '../../types/inventory'
import { Loader2, Plus, Camera, Upload } from 'lucide-react'
import ScanCardAlternatives from '../ScanCardAlternatives'

interface AddBuyCardProps {
  onAdd: (cardId: string, condition: StandardCondition | null, price: number | null, locale: Locale) => Promise<void>
}

function convertToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
    reader.readAsDataURL(file)
  })
}

export function AddBuyCard({ onAdd }: AddBuyCardProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [cardData, setCardData] = useState<FullPokemonCard | null>(null)
  const [possibleCards, setPossibleCards] = useState<FullPokemonCard[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [condition, setCondition] = useState<StandardCondition>('NearMint')
  const [price, setPrice] = useState<string>('0')

  const API_URL = getApiUrl()

  const analyzeCard = async (imageFile: File) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_URL}/api/analyze-card`, {
        method: 'POST',
        credentials: 'include',
        body: imageFile,
      })
      if (!response.ok) {
        const errData = (await response.json().catch(() => ({}))) as { error?: string; message?: string }
        throw new Error(errData.error || errData.message || 'Failed to analyze card')
      }
      const data = await response.json()
      if (data.bestMatch !== undefined && Array.isArray(data.possibleCards)) {
        setCardData(data.bestMatch)
        setPossibleCards(data.possibleCards)
      } else {
        setCardData(data)
        setPossibleCards([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file')
      return
    }
    try {
      const base64 = await convertToBase64(file)
      setImagePreview(base64)
      await analyzeCard(file)
    } catch {
      setError('Failed to process image')
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) handleFile(files[0])
  }

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files?.length) handleFile(files[0])
  }

  const handleSelectAlternative = (card: FullPokemonCard) => {
    setCardData(card)
  }

  useEffect(() => {
    if (cardData?.cardMarketPrice?.avg != null) {
      setPrice(String(cardData.cardMarketPrice.avg))
    } else {
      setPrice('0')
    }
  }, [cardData])

  const handleAdd = async () => {
    if (!cardData) return
    const priceNum = price.trim() ? parseFloat(price) : null
    if (price.trim() && (isNaN(priceNum!) || priceNum! < 0)) {
      setError('Please enter a valid price')
      return
    }
    setIsAdding(true)
    setError(null)
    try {
      await onAdd(cardData.id, condition, priceNum, cardData.set.locale ?? 'en')
      setImagePreview(null)
      setCardData(null)
      setPossibleCards([])
      setPrice('0')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card')
    } finally {
      setIsAdding(false)
    }
  }

  const resetCard = () => {
    setImagePreview(null)
    setCardData(null)
    setPossibleCards([])
    setError(null)
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <Label className="text-gray-700 mb-2 block">Add card (scan image)</Label>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all min-h-[120px] flex flex-col items-center justify-center gap-2 ${
          imagePreview ? 'border-gray-200' : 'border-blue-300 bg-blue-50/30 hover:border-blue-400'
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {!imagePreview ? (
          <>
            <p className="text-sm text-gray-600 m-0">Drag & drop a card image or</p>
            <div className="flex gap-2">
              <label className="inline-flex items-center gap-2 py-2 px-4 bg-blue-800 text-white rounded-lg cursor-pointer text-sm font-medium hover:bg-blue-900">
                <Upload className="h-4 w-4" />
                Choose File
                <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
              </label>
              <label className="inline-flex items-center gap-2 py-2 px-4 bg-gray-700 text-white rounded-lg cursor-pointer text-sm font-medium hover:bg-gray-800">
                <Camera className="h-4 w-4" />
                Take Photo
                <input type="file" accept="image/*" capture="environment" onChange={handleFileInput} className="hidden" />
              </label>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 w-full">
            <img src={imagePreview} alt="Card" className="max-h-24 object-contain rounded" />
            <Button variant="outline" size="sm" onClick={resetCard}>
              Scan another
            </Button>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Analyzing card...
        </div>
      )}

      {error && !loading && <p className="text-sm text-red-600 mt-2">{error}</p>}

      {cardData && !loading && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
            {cardData.cardData.imageUrl ? <img src={cardData.cardData.imageUrl} alt="" className="w-12 h-16 object-contain rounded" /> : null}
            <div>
              <p className="font-medium text-gray-900 m-0">{cardData.name}</p>
              {cardData.set && <p className="text-sm text-muted-foreground m-0">{cardData.set.name}</p>}
            </div>
          </div>

          <ScanCardAlternatives
            possibleCards={possibleCards.filter((c) => c.id !== cardData.id)}
            selectedCard={cardData}
            selectedCardId={cardData.id}
            onSelectCard={handleSelectAlternative}
          />

          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-sm">Condition</Label>
              <Select value={condition} onValueChange={(v) => setCondition(v as StandardCondition)}>
                <SelectTrigger className="h-8 w-[120px] mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STANDARD_CONDITIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Max price ($)</Label>
              <Input
                type="text"
                value={price}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === '' || /^\d*\.?\d*$/.test(v)) setPrice(v)
                }}
                placeholder="0.00"
                className="w-20 h-8 mt-1 text-sm"
              />
            </div>
            <Button onClick={handleAdd} disabled={isAdding} className="bg-blue-800 text-white hover:bg-blue-900">
              {isAdding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Add to offer
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
